import React, {
  useEffect, useMemo, useState,
} from 'react';

import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import debounce from 'lodash/debounce';

import { useEventListFiltersTranslation } from '../../../i18n';
import { useEventListFilters } from '../../../useEventListFilters';
import st from '../../popovers.module.sass';

type AddressField = 'cities' | 'areas' | 'districts' | 'streets' | 'house_numbers';

type AddressValues = Record<AddressField, string>;

type AddressErrors = Partial<Record<AddressField, string>>;

const ADDRESS_MAX_LENGTH = 36;

const ADDRESS_FIELDS: AddressField[] = ['cities', 'areas', 'districts', 'streets', 'house_numbers'];

const emptyValues: AddressValues = {
  cities: '',
  areas: '',
  districts: '',
  streets: '',
  house_numbers: '',
};

const parseValues = (value: string): string[] | undefined => {
  const items = value.split(',').map((item) => item.trim()).filter(Boolean);
  return items.length > 0 ? items : undefined;
};

const isLengthInvalid = (value: string): boolean => parseValues(value)
  ?.some((item) => item.length > ADDRESS_MAX_LENGTH) ?? false;

function getAddressErrors(values: AddressValues): AddressErrors {
  const errors: AddressErrors = {};

  ADDRESS_FIELDS.forEach((field) => {
    if (isLengthInvalid(values[field])) {
      errors[field] = String(validate.maxLength(ADDRESS_MAX_LENGTH).message);
    }
  });

  return errors;
}

function getFilterValues(values: AddressValues): Partial<Record<AddressField, string[]>> {
  return ADDRESS_FIELDS.reduce<Partial<Record<AddressField, string[]>>>((result, field) => ({
    ...result,
    [field]: parseValues(values[field]),
  }), {});
}

export function AddressTab(): React.JSX.Element {
  const { t } = useEventListFiltersTranslation();
  const { filters, updateFilters } = useEventListFilters();

  const [local, setLocal] = useState<AddressValues>(emptyValues);
  const errors = getAddressErrors(local);

  const applyFilters = useMemo(() => debounce(updateFilters, 200), [updateFilters]);

  useEffect(() => {
    setLocal({
      cities: filters.cities?.join(',') ?? '',
      areas: filters.areas?.join(',') ?? '',
      districts: filters.districts?.join(',') ?? '',
      streets: filters.streets?.join(',') ?? '',
      house_numbers: filters.house_numbers?.join(',') ?? '',
    });
  }, [filters]);

  const handleChange = (field: AddressField, value: string) => {
    const nextValues = { ...local, [field]: value };
    setLocal(nextValues);

    if (Object.keys(getAddressErrors(nextValues)).length > 0) {
      applyFilters.cancel();
      return;
    }
    applyFilters(getFilterValues(nextValues));
  };

  const handleClear = (field: AddressField) => () => handleChange(field, '');

  return (
    <div className={st.AddressTabWrapper}>
      <div>
        <Control.Input
          errorMessage={errors.cities}
          hasError={!!errors.cities}
          id="cities"
          isClearable
          label={t('cities')}
          name="cities"
          onChangeValue={(value) => handleChange('cities', value)}
          onClear={handleClear('cities')}
          value={local.cities}
        />
      </div>
      <div>
        <Control.Input
          errorMessage={errors.areas}
          hasError={!!errors.areas}
          id="areas"
          isClearable
          label={t('areas')}
          name="areas"
          onChangeValue={(value) => handleChange('areas', value)}
          onClear={handleClear('areas')}
          value={local.areas}
        />
      </div>
      <div>
        <Control.Input
          errorMessage={errors.districts}
          hasError={!!errors.districts}
          id="districts"
          isClearable
          label={t('districts')}
          name="districts"
          onChangeValue={(value) => handleChange('districts', value)}
          onClear={handleClear('districts')}
          value={local.districts}
        />
      </div>
      <div className={st.RowInputsWrapper}>
        <div className={st.AddressStreet}>
          <Control.Input
            errorMessage={errors.streets}
            hasError={!!errors.streets}
            id="streets"
            isClearable
            label={t('streets')}
            name="streets"
            onChangeValue={(value) => handleChange('streets', value)}
            onClear={handleClear('streets')}
            value={local.streets}
          />
        </div>
        <div className={st.AddressHouse}>
          <Control.Input
            errorMessage={errors.house_numbers}
            hasError={!!errors.house_numbers}
            id="house_numbers"
            isClearable
            label={t('house_numbers')}
            name="house_numbers"
            onChangeValue={(value) => handleChange('house_numbers', value)}
            onClear={handleClear('house_numbers')}
            value={local.house_numbers}
          />
        </div>
      </div>
    </div>
  );
}
